import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import usePermissions from '../../hooks/usePermissions';

/**
 * CreateRootPermissionModal component
 * Modal for creating a new root-level permission
 */
const CreateRootPermissionModal = ({ open, category, onClose, onSuccess }) => {
  const { createPermission, validatePath, loading } = usePermissions();

  const [formData, setFormData] = useState({
    name: '',
    path: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [pathValidation, setPathValidation] = useState({ checking: false, result: null });
  const [submitError, setSubmitError] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({ name: '', path: '', description: '' });
      setErrors({});
      setPathValidation({ checking: false, result: null });
      setSubmitError(null);
    }
  }, [open]);

  // Validate path in real-time (debounced)
  useEffect(() => {
    if (!formData.path || formData.path.length < 2) {
      setPathValidation({ checking: false, result: null });
      return;
    }

    const timer = setTimeout(async () => {
      setPathValidation({ checking: true, result: null });
      try {
        const result = await validatePath(formData.path);
        setPathValidation({ checking: false, result: result });
      } catch (err) {
        console.log('error')
        setPathValidation({ checking: false, result: null });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.path, validatePath]);
console.log(pathValidation);
  // Handle form field changes
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: '' }));
    setSubmitError(null);
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.path.trim()) {
      newErrors.path = 'Path is required';
    } else if (!/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/.test(formData.path)) {
      newErrors.path = 'Path must follow format: lowercase letters, numbers, underscores, and dots only. Must start with a letter.';
    }

    if (pathValidation.result && !pathValidation.result.available) {
      newErrors.path = pathValidation.result.message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSubmitError(null);
      await createPermission({
        ...formData,
        category
      });
      onSuccess();
    } catch (err) {
      setSubmitError(err.message || err || 'Failed to create permission');
    }
  };

  // Get path input end adornment (icon/spinner)
  const getPathEndAdornment = () => {
    if (pathValidation.checking) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} />
        </InputAdornment>
      );
    }

    if (pathValidation.result) {
      if (pathValidation.result.available) {
        return (
          <InputAdornment position="end">
            <CheckCircleIcon color="success" />
          </InputAdornment>
        );
      } else {
        return (
          <InputAdornment position="end">
            <ErrorIcon color="error" />
          </InputAdornment>
        );
      }
    }

    return null;
  };

  // Get TextField color based on validation
  const getPathColor = () => {
    if (pathValidation.result) {
      return pathValidation.result.available ? 'success' : 'error';
    }
    return undefined;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create Root Permission
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          Category: {category === 'system' ? 'System' : 'Project'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Name Field */}
          <TextField
            label="Permission Name"
            fullWidth
            required
            value={formData.name}
            onChange={handleChange('name')}
            error={Boolean(errors.name)}
            helperText={errors.name || 'Display name for the permission'}
            placeholder="e.g., Dashboard, User Management, Project Settings"
          />

          {/* Path Field */}
          <TextField
            label="Permission Path"
            fullWidth
            required
            value={formData.path}
            onChange={handleChange('path')}
            error={Boolean(errors.path)}
            helperText={
              errors.path ||
              pathValidation.result?.message ||
              'Unique path identifier (e.g., dashboard, user_management, project.settings)'
            }
            placeholder="e.g., dashboard"
            color={getPathColor()}
            InputProps={{
              endAdornment: getPathEndAdornment()
            }}
            sx={{ fontFamily: 'monospace' }}
          />

          {/* Description Field */}
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={handleChange('description')}
            error={Boolean(errors.description)}
            helperText={errors.description || 'Optional description of what this permission controls'}
            placeholder="Describe what this permission allows users to do..."
          />

          {/* Path Format Help */}
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="caption" component="div">
              <strong>Path Format Rules:</strong>
            </Typography>
            <Typography variant="caption" component="ul" sx={{ mt: 0.5, pl: 2 }}>
              <li>Must start with a lowercase letter</li>
              <li>Can contain lowercase letters, numbers, and underscores</li>
              <li>Use dots (.) to create hierarchy (e.g., dashboard.filter)</li>
              <li>Cannot be changed after creation</li>
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            pathValidation.checking ||
            (pathValidation.result && !pathValidation.result.available)
          }
        >
          {loading ? 'Creating...' : 'Create Permission'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRootPermissionModal;
